// import components
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';

import EmployeeSelector, {
  Employee,
  EmployeeSelectorHandle
} from './EmployeeSelector';

// import external libraries
import { camelCase, sortBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import styles
import {
  StyledIcon,
  StyledOverflowXScrollBox,
  StyledPreferredEmployeesBox,
  StyledPreferredEmployeesTableRow
} from './PreferredEmployees.styles';
import { StyledBoxFlexCenter } from './Box/Box.styles';

// import types
import {
  PreferredEmployee,
  PreferredEmployeesProps
} from './PreferredEmployees.types';
import { Icon } from '@sdb/planning-ui';
import useGetUsersEmployees from 'components/flex-portal/FlexMatch/hooks/useGetUsersEmployees';

export default function PreferredEmployees(props: PreferredEmployeesProps) {
  const {
    date,
    employees,
    disablePortal,
    readOnly,
    createdBy,
    onlyFlex,
    showProposed,
    addEmployee,
    removeEmployee,
    setSelectedEmployees,
    fieldState,
    disabled = false
  } = props;
  const { t } = useTranslation('common');
  const [preferredEmployees, setPreferredEmployees] = useState<
    PreferredEmployee[]
  >(employees || []);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const employeeSelectorRef = useRef<EmployeeSelectorHandle | null>(null);

  const { data: usersEmployees = [] } = useGetUsersEmployees(
    { date, onlyFlex },
    {
      enabled: !!date && !readOnly
    }
  );

  const usersEmployeesFetched = usersEmployees.length > 0;

  const onEmployeeSelected = (options: Employee[]) => {
    if (preferredEmployees.length < options.length) {
      const employee = preferredEmployees.length
        ? options.filter(
            ({ employeeId }) =>
              !preferredEmployees
                .map(({ employeeId }) => employeeId)
                .includes(employeeId)
          )[0]
        : options[0];

      setSelectedEmployees &&
        setSelectedEmployees?.((prev) => [...prev, { ...employee, createdBy }]);

      if (addEmployee) {
        addEmployee(employee)?.then(
          () => {
            setPreferredEmployees([
              ...preferredEmployees,
              { ...employee, createdBy }
            ]);
          },
          () => {
            employeeSelectorRef?.current?.uncheckEmployee(employee, false);
          }
        );
      }
    } else {
      const employee = preferredEmployees.filter(
        ({ employeeId }) =>
          !options.map(({ employeeId }) => employeeId).includes(employeeId)
      )[0];

      setSelectedEmployees &&
        setSelectedEmployees((prev: PreferredEmployee[]) =>
          prev.filter(
            (emp) => !(emp.id === employee.id || emp.empId === employee.id)
          )
        );

      if (removeEmployee) {
        removeEmployee(employee)?.then(
          () => {
            setPreferredEmployees(
              preferredEmployees.filter(
                ({ employeeId }) => employeeId !== employee.employeeId
              )
            );
          },
          () => {
            employeeSelectorRef?.current?.checkEmployee(employee, false);
          }
        );
      }
    }
  };

  useEffect(() => {
    if (employees && allEmployees.length) {
      const mappedEmployees = employees
        .map(({ employeeId, createdBy, remark, empId }) => {
          const employee = allEmployees.find(
            (e) => e.employeeId === employeeId || e.employeeId === empId
          );
          if (!employee) {
            return null;
          }
          return { ...employee, createdBy, remark };
        })
        .flatMap((e) => (e ? [e] : []));

      setPreferredEmployees(mappedEmployees);
      employeeSelectorRef?.current?.setSelectedEmployees(mappedEmployees);
    }
  }, [employees, allEmployees]);

  useEffect(() => {
    if (date && usersEmployeesFetched) {
      setAllEmployees(
        usersEmployees?.map(({ empId, nameInformal, nr }) => ({
          employeeId: empId,
          employeeName: nameInformal,
          employeeNr: nr,
          id: empId,
          nr,
          label: `${nr} - ${nameInformal}`
        }))
      );
    }
  }, [date, createdBy, usersEmployeesFetched]);

  // If readOnly, allEmployees is not needed, so we can still render current list
  if (!usersEmployeesFetched && !readOnly) return null;

  return (
    <StyledPreferredEmployeesBox>
      {!readOnly && (
        <EmployeeSelector
          ref={employeeSelectorRef}
          options={allEmployees}
          isMulti
          disablePortal={disablePortal}
          onSelected={onEmployeeSelected}
          disabled={disabled}
          variant="outlined"
          fieldState={fieldState}
          fullWidth
        />
      )}
      {((employees && employees?.length > 0) ||
        preferredEmployees.length > 0) && (
        <StyledOverflowXScrollBox>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left" width="30px">
                  <strong>#</strong>
                </TableCell>
                <TableCell align="left" width="30px">
                  <strong>{t('employeeName')}</strong>
                </TableCell>
                {!showProposed && createdBy && (
                  <TableCell align="center">
                    <strong>{t('employeeType')}</strong>
                  </TableCell>
                )}
                {!readOnly && <TableCell align="center" width={16} />}
              </TableRow>
            </TableHead>
            {(readOnly ? employees || [] : preferredEmployees).length > 0 && (
              <TableBody>
                {sortBy(
                  readOnly ? employees : preferredEmployees,
                  'employeeName'
                ).map((employee) => {
                  const {
                    employeeId,
                    employeeName,
                    employeeNr,
                    createdBy,
                    remark
                  } = employee;
                  return (
                    <StyledPreferredEmployeesTableRow key={employeeId}>
                      <TableCell align="left" width="30px">
                        {employeeNr}
                      </TableCell>
                      <TableCell align="left" width="30px">
                        <StyledBoxFlexCenter>
                          {employeeName}
                          {remark && (
                            <Tooltip title={remark}>
                              <StyledIcon type="note" fontSize="inherit" />
                            </Tooltip>
                          )}
                        </StyledBoxFlexCenter>
                      </TableCell>
                      {!showProposed && createdBy && (
                        <TableCell align="center">
                          {t(camelCase(createdBy))}
                        </TableCell>
                      )}
                      {!readOnly && (
                        <TableCell align="center" width={16}>
                          <IconButton
                            onClick={() =>
                              employeeSelectorRef?.current?.uncheckEmployee(
                                employee
                              )
                            }
                            size="small"
                          >
                            <Icon type="xmark" fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </StyledPreferredEmployeesTableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </StyledOverflowXScrollBox>
      )}
    </StyledPreferredEmployeesBox>
  );
}
